// ============================================================
// [2.12] Rôles et permissions d'un serveur Siverix : libellés et explications
// en français, groupes de l'éditeur, hiérarchie des rôles (fonctions pures),
// réordonnancement, surcharges de salon à trois états, couleurs, version
// minimale du serveur. Partagé par l'API, le bureau et la console d'admin.
// ============================================================
import { Permission, parsePermissions } from './permissions.js';
export const ADMINISTRATOR_WARNING = 'Attention : cette permission donne tous les droits du serveur et passe outre les réglages des salons. Ne la donne qu’à des personnes de confiance.';
export const PERMISSION_INFO = {
    VIEW_CHANNEL: {
        flag: 'VIEW_CHANNEL',
        label: 'Voir les salons',
        description: 'Permet de voir les salons. Sans elle, un salon n’apparaît pas dans la liste.',
        channelLabel: 'Voir le salon',
        channelDescription: 'Sans elle, ce salon n’apparaît pas dans la liste et rien n’y est possible.',
    },
    READ_MESSAGE_HISTORY: {
        flag: 'READ_MESSAGE_HISTORY',
        label: 'Lire les messages',
        description: 'Permet de lire les messages des salons, les anciens comme les nouveaux.',
        channelDescription: 'Permet de lire les messages de ce salon, les anciens comme les nouveaux.',
    },
    SEND_MESSAGES: {
        flag: 'SEND_MESSAGES',
        label: 'Envoyer des messages',
        description: 'Permet d’écrire dans les salons textuels.',
        channelDescription: 'Permet d’écrire dans ce salon.',
    },
    EMBED_LINKS: {
        flag: 'EMBED_LINKS',
        label: 'Aperçu des liens',
        description: 'Permet d’afficher un aperçu des liens envoyés.',
    },
    ATTACH_FILES: {
        flag: 'ATTACH_FILES',
        label: 'Joindre des fichiers',
        description: 'Permet d’envoyer des images et des fichiers avec ses messages.',
    },
    ADD_REACTIONS: {
        flag: 'ADD_REACTIONS',
        label: 'Ajouter des réactions',
        description: 'Permet d’ajouter des réactions aux messages.',
    },
    USE_EXTERNAL_EMOJIS: {
        flag: 'USE_EXTERNAL_EMOJIS',
        label: 'Emojis d’autres serveurs',
        description: 'Permet d’utiliser les emojis personnalisés d’autres serveurs.',
    },
    MENTION_EVERYONE: {
        flag: 'MENTION_EVERYONE',
        label: 'Mentionner @everyone et @here',
        description: 'Permet d’utiliser @everyone et @here pour prévenir tous les membres.',
    },
    MANAGE_MESSAGES: {
        flag: 'MANAGE_MESSAGES',
        label: 'Gérer les messages',
        description: 'Permet de supprimer les messages des autres, d’épingler des messages et d’écrire sans attendre en mode lent.',
    },
    VOICE_CONNECT: {
        flag: 'VOICE_CONNECT',
        label: 'Se connecter',
        description: 'Permet de rejoindre les salons vocaux.',
        channelDescription: 'Permet de rejoindre ce salon vocal.',
    },
    VOICE_SPEAK: {
        flag: 'VOICE_SPEAK',
        label: 'Parler',
        description: 'Permet de parler dans les salons vocaux.',
    },
    VOICE_VIDEO: {
        flag: 'VOICE_VIDEO',
        label: 'Caméra',
        description: 'Permet d’allumer sa caméra dans les salons vocaux.',
    },
    VOICE_SCREEN_SHARE: {
        flag: 'VOICE_SCREEN_SHARE',
        label: 'Partager son écran',
        description: 'Permet de partager son écran dans les salons vocaux.',
    },
    VOICE_MUTE_MEMBERS: {
        flag: 'VOICE_MUTE_MEMBERS',
        label: 'Couper le micro des membres',
        description: 'Permet de couper ou de rendre le micro d’un membre placé sous soi.',
    },
    VOICE_MOVE_MEMBERS: {
        flag: 'VOICE_MOVE_MEMBERS',
        label: 'Déplacer des membres',
        description: 'Permet de déplacer vers un autre salon vocal, ou de déconnecter, un membre placé sous soi. Permet aussi d’entrer dans un salon vocal plein.',
    },
    MANAGE_CHANNELS: {
        flag: 'MANAGE_CHANNELS',
        label: 'Gérer les salons',
        description: 'Permet de créer, modifier, déplacer et supprimer les salons et les catégories, et de gérer les emojis du serveur.',
    },
    CREATE_INVITE: {
        flag: 'CREATE_INVITE',
        label: 'Créer des invitations',
        description: 'Permet de créer des liens d’invitation vers le serveur.',
    },
    KICK_MEMBERS: {
        flag: 'KICK_MEMBERS',
        label: 'Expulser des membres',
        description: 'Permet de retirer du serveur un membre placé sous soi. Il pourra revenir avec une invitation.',
    },
    BAN_MEMBERS: {
        flag: 'BAN_MEMBERS',
        label: 'Bannir des membres',
        description: 'Permet d’empêcher un membre de revenir sur le serveur.',
    },
    MANAGE_NICKNAMES: {
        flag: 'MANAGE_NICKNAMES',
        label: 'Gérer les pseudos',
        description: 'Permet de changer le pseudo des autres membres.',
    },
    MANAGE_ROLES: {
        flag: 'MANAGE_ROLES',
        label: 'Gérer les rôles',
        description: 'Permet de créer, modifier et supprimer les rôles placés sous son rôle le plus haut, de les donner aux membres et de régler les permissions des salons.',
    },
    MANAGE_COMMUNITY: {
        flag: 'MANAGE_COMMUNITY',
        label: 'Gérer le serveur',
        description: 'Permet de changer le nom, la description, l’icône et la visibilité du serveur.',
    },
    VIEW_AUDIT_LOG: {
        flag: 'VIEW_AUDIT_LOG',
        label: 'Voir le journal',
        description: 'Permet de voir l’historique des actions de modération du serveur.',
    },
    MANAGE_EVENTS: {
        flag: 'MANAGE_EVENTS',
        label: 'Gérer les événements',
        description: 'Permet de créer des événements, et de modifier ou d’annuler ceux des autres.',
    },
    ADMINISTRATOR: {
        flag: 'ADMINISTRATOR',
        label: 'Administrateur',
        description: 'Donne toutes les permissions du serveur et passe outre les réglages des salons. Ne permet pas d’agir sur un rôle placé au niveau du sien ou au-dessus.',
        warning: ADMINISTRATOR_WARNING,
    },
    INSTANCE_ADMIN: {
        flag: 'INSTANCE_ADMIN',
        label: 'Administration de l’instance',
        description: 'Réservé à l’administrateur de l’instance ; ne se donne pas par un rôle.',
    },
};
/** Éditeur de rôle : groupes affichés, dans l'ordre (bits cachés exclus). */
export const ROLE_PERMISSION_GROUPS = [
    {
        id: 'server',
        label: 'Gestion du serveur',
        flags: [
            'MANAGE_COMMUNITY',
            'MANAGE_ROLES',
            'MANAGE_CHANNELS',
            'CREATE_INVITE',
            'MANAGE_EVENTS',
        ],
    },
    { id: 'members', label: 'Membres', flags: ['KICK_MEMBERS'] },
    { id: 'channels', label: 'Salons', flags: ['VIEW_CHANNEL'] },
    {
        id: 'messages',
        label: 'Messages',
        flags: [
            'READ_MESSAGE_HISTORY',
            'SEND_MESSAGES',
            'ATTACH_FILES',
            'ADD_REACTIONS',
            'MENTION_EVERYONE',
            'MANAGE_MESSAGES',
        ],
    },
    {
        id: 'voice',
        label: 'Vocal',
        flags: [
            'VOICE_CONNECT',
            'VOICE_SPEAK',
            'VOICE_VIDEO',
            'VOICE_SCREEN_SHARE',
            'VOICE_MUTE_MEMBERS',
            'VOICE_MOVE_MEMBERS',
        ],
    },
    { id: 'advanced', label: 'Avancé', flags: ['ADMINISTRATOR'] },
];
/** Toutes les permissions présentées dans l'éditeur de rôle, à plat. */
export const EDITABLE_ROLE_FLAGS = ROLE_PERMISSION_GROUPS.flatMap((g) => g.flags);
/** Masque des permissions présentées dans l'éditeur de rôle. */
export const EDITABLE_ROLE_MASK = EDITABLE_ROLE_FLAGS.reduce((acc, f) => acc | Permission[f], 0n);
/** Onglet Permissions d'un salon : les permissions qui ont un effet dans ce type de salon. */
export const CHANNEL_PERMISSION_FLAGS = {
    TEXT: [
        'VIEW_CHANNEL',
        'READ_MESSAGE_HISTORY',
        'SEND_MESSAGES',
        'ATTACH_FILES',
        'ADD_REACTIONS',
        'MENTION_EVERYONE',
        'MANAGE_MESSAGES',
    ],
    VOICE: [
        'VIEW_CHANNEL',
        'VOICE_CONNECT',
        'VOICE_SPEAK',
        'VOICE_VIDEO',
        'VOICE_SCREEN_SHARE',
        'VOICE_MUTE_MEMBERS',
        'VOICE_MOVE_MEMBERS',
    ],
};
/** Mêmes permissions, groupées pour l'onglet Permissions d'un salon. */
export function channelPermissionGroups(kind) {
    const flags = CHANNEL_PERMISSION_FLAGS[kind];
    return [
        { id: 'channel', label: 'Salon', flags: flags.filter((f) => f === 'VIEW_CHANNEL') },
        {
            id: kind === 'TEXT' ? 'messages' : 'voice',
            label: kind === 'TEXT' ? 'Messages' : 'Vocal',
            flags: flags.filter((f) => f !== 'VIEW_CHANNEL'),
        },
    ];
}
function fold(s) {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’']/g, "'").toLowerCase().trim();
}
/**
 * Filtre de la recherche de l'éditeur : libellé ou explication contient le
 * texte, sans tenir compte des accents ni de la casse. Texte vide : tout.
 */
export function searchPermissions(query, flags) {
    const q = fold(query);
    if (!q)
        return [...flags];
    return flags.filter((f) => {
        const info = PERMISSION_INFO[f];
        return (fold(info.label).includes(q) ||
            fold(info.description).includes(q) ||
            (info.channelLabel ? fold(info.channelLabel).includes(q) : false));
    });
}
// ---------- Hiérarchie des rôles ----------
export const NEW_ROLE_NAME = 'Nouveau rôle';
export const MAX_ROLES_PER_COMMUNITY = 250;
export const OWNER_RANK = Number.POSITIVE_INFINITY;
export const INSTANCE_ADMIN_RANK = Number.MAX_SAFE_INTEGER;
/** Position du rôle le plus haut parmi `roleIds` (@everyone et rôles inconnus ignorés) ; 0 sinon. */
export function highestRolePosition(roleIds, roles) {
    const byId = new Map(roles.map((r) => [r.id, r]));
    let top = 0;
    for (const id of roleIds) {
        const r = byId.get(id);
        if (r && !r.isManaged && r.position > top)
            top = r.position;
    }
    return top;
}
/** Rang : propriétaire > administrateur de l'instance > rôle le plus haut. */
export function hierarchyRank(actor) {
    if (actor.isOwner)
        return OWNER_RANK;
    if (actor.isInstanceAdmin)
        return INSTANCE_ADMIN_RANK;
    return Math.max(0, actor.highestPosition);
}
/**
 * Peut-on modifier ce rôle (nom, couleur, permissions, suppression, ordre,
 * surcharge de salon) ? @everyone : oui (ses permissions seulement) ; sinon
 * seulement un rôle STRICTEMENT plus bas que son rôle le plus haut.
 */
export function canManageRole(actor, role) {
    if (role.isManaged)
        return true;
    return role.position < hierarchyRank(actor);
}
/** Peut-on donner ou retirer ce rôle ? Jamais @everyone ; sinon strictement plus bas que soi. */
export function canAssignRole(actor, role) {
    if (role.isManaged)
        return false;
    return role.position < hierarchyRank(actor);
}
/**
 * Peut-on agir sur ce membre (expulser, micro, déplacer, déconnecter,
 * surcharge de salon) ? Jamais sur le propriétaire ; sinon son rôle le plus
 * haut doit être STRICTEMENT plus bas que le sien.
 */
export function canActOnMember(actor, target) {
    if (target.isOwner)
        return false;
    return hierarchyRank(target) < hierarchyRank(actor);
}
/**
 * Anti-escalade : bits changés entre `before` et `after` que l'acteur n'a
 * pas lui-même. 0n = changement permis. Propriétaire (tous les bits),
 * ADMINISTRATOR et administration de l'instance : aucune limite.
 */
export function forbiddenPermissionChange(before, after, actorPerms) {
    if ((actorPerms & (Permission.ADMINISTRATOR | Permission.INSTANCE_ADMIN)) !== 0n)
        return 0n;
    const changed = before ^ after;
    return changed & ~actorPerms;
}
/** Du plus haut au plus bas ; ex aequo : le plus récent d'abord puis l'id ; @everyone en dernier. */
export function sortRolesForDisplay(roles) {
    return [...roles].sort((a, b) => {
        if (a.isManaged !== b.isManaged)
            return a.isManaged ? 1 : -1;
        if (a.position !== b.position)
            return b.position - a.position;
        const ca = a.createdAt ?? '';
        const cb = b.createdAt ?? '';
        if (ca !== cb)
            return ca < cb ? 1 : -1;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
}
/** Déplace `id` à l'index `toIndex` (borné) d'une liste d'ids ; liste inchangée si absent. */
export function moveRoleInOrder(ids, id, toIndex) {
    const from = ids.indexOf(id);
    if (from < 0)
        return [...ids];
    const next = ids.filter((x) => x !== id);
    const to = Math.max(0, Math.min(next.length, Math.trunc(toIndex)));
    next.splice(to, 0, id);
    return next;
}
/** Garde de type explicite (l'API se construit sans `strict`). */
export function isRoleReorderOk(plan) {
    return plan.ok === true;
}
/**
 * Plan du réordonnancement atomique (PUT /communities/:cid/roles/positions).
 * `order` : ids du plus haut au plus bas, sans @everyone, tous les autres
 * rôles du serveur exactement une fois. Les rôles que l'acteur ne peut pas
 * gérer (au niveau du sien ou au-dessus) doivent rester en tête, dans le même
 * ordre : on ne bouge que des rôles placés sous le sien, et jamais au niveau
 * du sien ni au-dessus. Positions : order[0] = N … order[N-1] = 1.
 *
 * La renumérotation départage les rôles placés au même rang. Un rôle
 * verrouillé (au niveau du rôle le plus haut de l'acteur ou au-dessus) ne
 * finit jamais sous une position de l'acteur : sinon un rôle « au même rang »
 * que le sien passerait sous lui et deviendrait modifiable, donnable,
 * expulsable. Rôles de l'acteur inconnus (`roleIds` absent) : deux rôles
 * verrouillés au rang de l'acteur suffisent à refuser, faute de savoir lequel
 * est le sien.
 */
export function planRoleReorder(roles, order, actor) {
    const movable = roles.filter((r) => !r.isManaged);
    const byId = new Map(movable.map((r) => [r.id, r]));
    if (order.length !== movable.length || new Set(order).size !== order.length) {
        return { ok: false, error: 'InvalidRoleOrder' };
    }
    for (const id of order)
        if (!byId.has(id))
            return { ok: false, error: 'InvalidRoleOrder' };
    const current = sortRolesForDisplay(movable);
    const locked = current.filter((r) => !canManageRole(actor, r));
    for (let i = 0; i < locked.length; i++) {
        if (order[i] !== locked[i].id)
            return { ok: false, error: 'RoleHierarchy' };
    }
    const n = order.length;
    const next = new Map(order.map((id, i) => [id, n - i]));
    if (locked.length > 0 && lockedEndsBelowActor(locked, next, actor)) {
        return { ok: false, error: 'RoleHierarchy' };
    }
    const changes = [];
    order.forEach((id) => {
        const position = next.get(id);
        if (byId.get(id).position !== position)
            changes.push({ id, position });
    });
    return { ok: true, changes };
}
/** Un rôle verrouillé finirait-il sous une position de l'acteur (voir planRoleReorder) ? */
function lockedEndsBelowActor(locked, next, actor) {
    if (actor.roleIds) {
        const mine = new Set(actor.roleIds.filter((id) => next.has(id)));
        let top = 0;
        for (const id of mine)
            top = Math.max(top, next.get(id));
        return locked.some((r) => !mine.has(r.id) && next.get(r.id) < top);
    }
    const atMyRank = locked.filter((r) => r.position === hierarchyRank(actor));
    return atMyRank.length > 1;
}
/**
 * Création d'un rôle : il prend la position 1, juste au-dessus de @everyone ;
 * les autres rôles montent d'un cran. Renvoie ces décalages.
 */
export function positionsForNewRole(roles) {
    return roles
        .filter((r) => !r.isManaged)
        .map((r) => ({ id: r.id, position: Math.max(1, r.position) + 1 }));
}
export const OVERRIDE_STATE_LABELS = {
    deny: 'Refuser',
    neutral: 'Neutre',
    allow: 'Autoriser',
};
export function overrideStateFor(allow, deny, bit) {
    if ((allow & bit) !== 0n)
        return 'allow';
    if ((deny & bit) !== 0n)
        return 'deny';
    return 'neutral';
}
/** Pose un état sur un bit ; allow et deny ne portent jamais le même bit. */
export function withOverrideState(current, bit, state) {
    let allow = current.allow & ~bit;
    let deny = current.deny & ~bit;
    if (state === 'allow')
        allow |= bit;
    else if (state === 'deny')
        deny |= bit;
    return { allow, deny };
}
// ---------- Couleurs ----------
/** Pastilles proposées (entiers RVB). 0 = pas de couleur. */
export const ROLE_COLOR_PRESETS = [
    0x1abc9c, 0x2ecc71, 0x3498db, 0x9b59b6, 0xe91e63, 0xf1c40f, 0xe67e22, 0xe74c3c, 0x95a5a6,
    0x607d8b, 0x11806a, 0x1f8b4c, 0x206694, 0x71368a, 0xad1457, 0xc27c0e, 0xa84300, 0x992d22,
];
export function colorIntToHex(n) {
    const v = Number.isFinite(n) ? Math.max(0, Math.min(0xffffff, Math.trunc(n))) : 0;
    return `#${v.toString(16).padStart(6, '0')}`;
}
/** « #abc », « abc », « #aabbcc » ou « aabbcc » -> entier ; sinon null. */
export function parseHexColor(input) {
    const s = input.trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(s)) {
        return parseInt(s
            .split('')
            .map((c) => c + c)
            .join(''), 16);
    }
    if (/^[0-9a-f]{6}$/i.test(s))
        return parseInt(s, 16);
    return null;
}
// ---------- Version minimale du serveur ----------
export const ROLES_MIN_SERVER_VERSION = '2.12.0';
/** Compare deux versions « x.y.z » (suffixe ignoré) ; null si l'une est illisible. */
export function compareVersions(a, b) {
    const parse = (v) => {
        const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(v.trim());
        return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
    };
    const pa = parse(a);
    const pb = parse(b);
    if (!pa || !pb)
        return null;
    for (let i = 0; i < 3; i++) {
        if (pa[i] !== pb[i])
            return pa[i] < pb[i] ? -1 : 1;
    }
    return 0;
}
/**
 * Le serveur gère-t-il les rôles 2.12 (onglets Rôles et Permissions) ?
 * Version absente : non (serveur < 2.2). Version illisible : non plus. Les
 * images construites sur main avant la 2.12 annoncent un identifiant de
 * commit seul (« b02c7da ») : traitées comme 2.12, elles affichaient des
 * onglets dont les routes n'existent pas. Depuis la 2.12, l'API annonce
 * toujours « x.y.z » en tête (« 2.12.0+b02c7da » pour une telle image).
 */
export function serverSupportsRoles(version) {
    if (typeof version !== 'string' || version.trim() === '')
        return false;
    const c = compareVersions(version, ROLES_MIN_SERVER_VERSION);
    return c !== null && c >= 0;
}
// ---------- Erreurs ----------
export const ROLE_ERRORS = {
    hierarchy: 'RoleHierarchy',
    escalation: 'PermissionEscalation',
    managedRole: 'ManagedRole',
    invalidOrder: 'InvalidRoleOrder',
    invalidTarget: 'InvalidOverrideTarget',
    tooManyRoles: 'TooManyRoles',
};
export const ROLE_ERROR_MESSAGES = {
    roleHierarchy: 'Ce rôle est au niveau du tien ou au-dessus : tu ne peux pas le modifier.',
    assignHierarchy: 'Ce rôle est au niveau du tien ou au-dessus : tu ne peux pas le donner ni le retirer.',
    memberHierarchy: 'Ce membre a un rôle au niveau du tien ou au-dessus : tu ne peux pas faire ça.',
    owner: 'Le propriétaire du serveur ne peut pas être visé.',
    orderHierarchy: 'Tu ne peux déplacer que des rôles placés sous le tien, et pas au-dessus.',
    escalation: 'Tu ne peux donner que des permissions que tu as toi-même.',
    managedRole: 'Le rôle @everyone ne peut être ni supprimé, ni donné, ni déplacé.',
    invalidOrder: 'L’ordre envoyé ne correspond pas aux rôles du serveur.',
    invalidTarget: 'Il faut viser soit un rôle, soit un membre.',
    tooManyRoles: 'Ce serveur a déjà 250 rôles.',
};
export function roleEditOf(role) {
    return {
        name: role.name,
        color: role.color || 0,
        hoist: role.hoist === true,
        mentionable: role.mentionable === true,
        permissions: parsePermissions(role.permissions),
    };
}
export function sameRoleEdit(a, b) {
    return (a.name === b.name &&
        a.color === b.color &&
        a.hoist === b.hoist &&
        a.mentionable === b.mentionable &&
        a.permissions === b.permissions);
}
/**
 * Rôle modifié ailleurs pendant l'édition (`prev` -> `next`) : chaque champ
 * (et chaque permission) que je n'ai pas touché prend la nouvelle valeur ;
 * ceux que j'ai changés gardent la mienne. Le nom se compare comme dans
 * roleEditPatch (sans les espaces des bords) : un espace ajouté seul n'est
 * pas un changement et n'annule pas un renommage fait ailleurs.
 */
export function rebaseRoleEdit(draft, prev, next) {
    const mine = draft.permissions ^ prev.permissions;
    return {
        name: draft.name.trim() === prev.name ? next.name : draft.name,
        color: draft.color === prev.color ? next.color : draft.color,
        hoist: draft.hoist === prev.hoist ? next.hoist : draft.hoist,
        mentionable: draft.mentionable === prev.mentionable ? next.mentionable : draft.mentionable,
        permissions: (next.permissions & ~mine) | (draft.permissions & mine),
    };
}
/**
 * Corps du PATCH d'un rôle : seulement ce qui diffère du rôle tel qu'on le
 * connaît (`base`) ; un changement fait ailleurs sur un autre champ n'est
 * donc pas écrasé. Le nom part sans les espaces des bords. Rôle géré
 * (@everyone) : seulement ses permissions (le serveur ignore son nom, sa
 * couleur, « afficher séparément » et « mentionnable »).
 */
export function roleEditPatch(draft, base, isManaged) {
    const patch = {};
    if (draft.permissions !== base.permissions)
        patch.permissions = draft.permissions.toString();
    if (isManaged)
        return patch;
    const name = draft.name.trim();
    if (name !== base.name)
        patch.name = name;
    if (draft.color !== base.color)
        patch.color = draft.color;
    if (draft.hoist !== base.hoist)
        patch.hoist = draft.hoist;
    if (draft.mentionable !== base.mentionable)
        patch.mentionable = draft.mentionable;
    return patch;
}
